<template>
  <table
    border="0"
    class="el-date-table date-table"
    @click="handleClick($event.target)"
    @keyup.enter="handleClick($event.target)"
    @keyup.esc="closeDateTable"
    @mouseover="handleMouseOver($event.target)"
    @mouseleave="handleMouseLeave($event)"
  >
    <thead>
      <tr>
        <th
          v-for="(weekday, wIndex) in weekdays"
          :key="wIndex"
        >
          {{ weekday }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="(row, rowIndex) in rows">
        <tr
          v-if="showRow(row, rowIndex)"
          :key="rowIndex"
          class="el-date-table__row table-row"
        >
          <template v-for="(cell, key) in row">
            <td
              v-if="showCell(cell)"
              :key="key"
              v-tooltip="getTooltip(cell)"
              :tabindex="isCellTabable(cell) ? 0 : -1"
              :class="getCellClasses(cell)"
            >
              <span>{{ cell.text }}</span>
            </td>
          </template>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
  import { hasClass } from 'element-ui/src/utils/dom'
  import Locale from 'element-ui/src/mixins/locale'
  import moment from 'moment'
  import get from 'lodash-es/get'
  import DateUtils from 'utils/DateUtils'
  import Tooltip from 'utils/directives/Tooltip'
  import { mapGetters } from 'vuex'

  function clearHours(time) {
    const cloneDate = new Date(time)
    cloneDate.setHours(0, 0, 0, 0)
    return cloneDate.getTime()
  }

  export default {
    directives: {
      Tooltip
    },
    mixins: [Locale],

    props: {
      firstDayOfWeek: {
        default: 7,
        type: Number,
        validator: (val) => val >= 1 && val <= 7
      },
      date: {
        type: [Date, String, Object],
        default: ''
      },
      year: {
        type: Number,
        default: 2020
      },
      month: {
        type: Number,
        default: 1
      },
      week: {
        type: String,
        default: ''
      },
      selectionMode: {
        type: String,
        default: 'day'
      },
      disabledDate: {
        type: Function,
        default: () => {}
      },
      minDate: {
        type: Date,
        default: new Date()
      },
      maxDate: {
        type: [Date, String, Object],
        default: ''
      },
      isReturn: {
        type: Boolean,
        default: false
      },
      directFlights: {
        type: Object,
        required: false,
        default: () => null
      },
      customSelected: {
        type: Boolean,
        default: false
      },
      rangeState: {
        type: Object,
        default() {
          return {
            endDate: null,
            selecting: true,
            row: null,
            column: null
          }
        }
      },
      tooltipText: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        tableRows: [[], [], [], [], [], []],
        rangeData: {
          endDate: null,
          selecting: true,
          row: null,
          column: null
        }
      }
    },
    computed: {
      ...mapGetters('device', ['isMobile']),
      offsetDay() {
        const week = this.firstDayOfWeek
        return week > 3 ? 7 - week : -week
      },
      weekdays() {
        const week = this.firstDayOfWeek
        const weekdays = moment.weekdaysShort()
        return weekdays.concat(weekdays).slice(week, week + 7)
      },
      startDate() {
        return new Date(this.getStartDateOfMonth(this.year, this.month))
      },
      rows() {
        const month = this.month < 9 ? `0${this.month + 1}` : this.month + 1
        const date = moment(`${this.year}-${month}-01`)
        let day = date.day()
        const dateCountOfMonth = date.daysInMonth()

        day = day === 0 ? 7 : day

        const offset = this.offsetDay
        const rows = this.tableRows
        let count = 1
        let firstDayPosition
        const startDate = this.startDate.setHours(0, 0, 0, 0)
        const disabledDate = this.disabledDate
        const now = clearHours(new Date())
        const minDate = clearHours(this.minDate)
        const maxDate = clearHours(this.maxDate)

        for (let i = 0; i < 6; i++) {
          const row = rows[parseInt(i)]

          for (let j = 0; j < 7; j++) {
            let cell = row[parseInt(j)]
            if (!cell) {
              cell = {
                row: i,
                column: j,
                type: 'normal',
                inRange: false,
                start: false,
                end: false
              }
            }

            cell.type = 'normal'

            const index = i * 7 + j
            const time = new Date(startDate).setDate(
              new Date(startDate).getDate() + (index - offset)
            )
            if (this.customSelected) {
              cell.selected = this.date && moment(time).isSame(this.date)
            } else {
              cell.inRange = this.date && time >= minDate && time <= maxDate
              cell.start = this.date !== '' && minDate && time === minDate
              cell.end = this.maxDate && time === maxDate && maxDate >= minDate
            }

            const isPast = moment(time).diff(moment.now(), 'days') < 0
            if (isPast) {
              cell.type = 'past'
            }

            const isToday = time === now

            if (isToday) {
              cell.type = 'today'
            }

            if (i >= 0 && i <= 1) {
              if (i * 7 + j >= day + offset) {
                cell.text = count++
                if (count === 2) {
                  firstDayPosition = i * 7 + j
                }
              } else {
                cell.text = ''
                cell.type = 'empty'
              }
            } else if (count <= dateCountOfMonth) {
              cell.text = count++
              if (count === 2) {
                firstDayPosition = i * 7 + j
              }
            } else {
              cell.text = ''
              cell.type = 'empty'
            }

            cell.disabled =
              typeof disabledDate === 'function'
              && disabledDate(DateUtils.resetTime(new Date(time)))

            // ToDo: Should be refactored and eslint enabled
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.$set(row, j, cell)
          }
        }

        rows.firstDayPosition = firstDayPosition
        return rows
      }
    },
    watch: {
      'rangeState.endDate': function (newVal) {
        this.markRange(newVal)
      },

      minDate(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.rangeData.selecting = true
        } else if (!newVal) {
          this.rangeData.selecting = false
        }
      },

      maxDate(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.rangeData.selecting = true
          this.$emit('pick', {
            minDate: this.minDate,
            maxDate: this.maxDate
          })
        }
      }
    },
    mounted() {
      this.rangeData = this.rangeState
    },
    methods: {
      closeDateTable() {
        this.$emit('escClose')
      },
      getTooltip(cell) {
        if (this.isMobile) {
          return {
            class: 'js-disabled-tooltip'
          }
        }

        if (cell.disabled && cell.text && cell.type !== 'past') {
          return {
            content: this.tooltipText,
            position: 'top',
            class: 'js-calendar-tooltip volDateChange-tooltip'
          }
        } else {
          return {
            class: 'js-disabled-tooltip'
          }
        }
      },
      showRow(row, index) {
        if (index !== 5) {
          return true
        }

        let show = false
        row.map((cell) => {
          if (cell.type !== 'empty') {
            show = true
          }
        })
        return show
      },
      showCell(cell) {
        if (cell.row === 5) {
          return cell.type !== 'empty'
        }
        return true
      },
      isCellTabable(cell) {
        return (cell.type === 'normal' || cell.type === 'today') && !cell.disabled
      },
      getCellClasses(cell) {
        const classes = ['cell']

        if (cell.disabled) {
          classes.push('disabled')
          classes.push(cell.type)
        } else {
          if (this.isDateInDirectFlights(Number(cell.text))) {
            classes.push('direct')
          }

          if (cell.type === 'normal' || cell.type === 'today') {
            classes.push('available')

            if (cell.inRange) {
              classes.push('in-range')
              classes.push('highlighted')
            }

            if (cell.start) {
              classes.push('start-date')
              classes.push('selected')
            }

            if (cell.end) {
              classes.push('end-date')
              classes.push('selected')
            }

            if (cell.selected) {
              classes.push('selected')
            }
          } else {
            classes.push(cell.type)
          }
        }

        if (cell.type === 'today') {
          classes.push('today')
        }

        return classes.join(' ')
      },
      getStartDateOfMonth(year, month) {
        const result = new Date(year, month, 1)
        let day = result.getDay()
        if (day === 0) {
          day = 7
        }
        return result.setDate(result.getDate() - day)
      },
      isDateInDirectFlights(cell) {
        const pathParts = [this.year, this.month + 1, cell, 0]
        const path = pathParts.join('.')
        return get(this.directFlights, path) === 'direct'
      },

      getDateOfCell(target) {
        if (target.tagName === 'SPAN') {
          target = target.parentNode
        }

        let year = Number(this.year)
        let month = Number(this.month)

        const cellIndex = target.cellIndex
        const rowIndex = target.parentNode.rowIndex

        const cell = this.rows[parseInt(rowIndex - 1)][parseInt(cellIndex)]
        const text = cell.text
        if (text !== '') {
          const className = target.className

          if (className.indexOf('prev') !== -1) {
            if (month === 0) {
              year--
              month = 11
            } else {
              month--
            }
          } else if (className.indexOf('next') !== -1) {
            if (month === 11) {
              year++
              month = 0
            } else {
              month++
            }
          }
          const day = parseInt(text, 10)
          const date = new Date(year, month, day)

          return DateUtils.resetTime(date)
        } else {
          return null
        }
      },

      markRange(hovered) {
        const startDate = DateUtils.resetTime(this.startDate || new Date())
        let maxDate = this.maxDate
        let minDate = this.date !== '' ? this.minDate : ''
        const rows = this.rows

        if (!this.isReturn) {
          if (hovered < minDate || minDate === '') {
            minDate = hovered
          }
        } else if (hovered > maxDate) {
          maxDate = hovered
        }

        for (let i = 0, k = rows.length; i < k; i++) {
          const row = rows[parseInt(i)]
          for (let j = 0, l = row.length; j < l; j++) {
            const cell = row[parseInt(j)]
            const index = i * 7 + j
            const time = startDate.getTime() + 86400000 * (index - this.offsetDay)
            cell.inRange = time > clearHours(minDate) && time < clearHours(maxDate)
          }
        }
      },

      handleMouseOver(target) {
        this.$emit('changerange', {
          minDate: this.minDate,
          maxDate: this.maxDate,
          rangeState: this.rangeData
        })

        if (target.tagName !== 'TD') return
        const column = target.cellIndex
        const row = target.parentNode.rowIndex - 1
        if (
          row < 0
          || this.rows[`${row}`] === 'undefined'
          || this.rows[`${row}`][`${column}`] === 'undefined'
        )
          return
        if (
          this.rows[`${row}`][`${column}`].type === 'empty'
          || this.rows[`${row}`][`${column}`].disabled
        )
          return
        this.rangeData.endDate = this.getDateOfCell(target)
      },

      handleMouseLeave() {
        if (!this.isReturn) {
          this.markRange(this.date)
        } else {
          this.rangeData.endDate = this.maxDate
        }
      },

      handleClick(target) {
        if (target.tagName !== 'TD' && target.tagName !== 'SPAN') return
        if (hasClass(target, 'disabled')) return
        if (target.innerText === '') return
        if (target.tagName === 'SPAN' && hasClass(target.parentElement, 'disabled'))
          return

        const newDate = this.getDateOfCell(target)
        if (this.selectionMode === 'range') {
          let minDate = this.minDate
          let maxDate = this.maxDate

          if (!this.isReturn) {
            minDate = newDate
          } else {
            maxDate = newDate
          }
          this.rangeData.selecting = true

          // mark range
          if (!this.isReturn) {
            this.markRange(this.minDate)
          } else {
            this.markRange(this.maxDate)
          }

          this.$emit('pick', {
            minDate,
            maxDate
          })
        } else {
          this.$emit('pick', {
            minDate: newDate,
            maxDate: newDate
          })
        }
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  table {
    margin: auto;
    max-width: 275px;
    border-collapse: separate;
    border-spacing: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;

    thead tr {
      flex-grow: 1;
      justify-content: space-between;

      th {
        color: $grey-default;
        font-size: em(14);
        font-weight: 500;
        line-height: 16px;
        text-align: center;
        width: 39px;
      }
    }

    tbody {
      display: inline-block;

      .table-row {
        margin-bottom: 3px;
      }
    }

    td {
      position: relative;
      width: 39px;
      height: 39px;
      display: inline-block;
      line-height: 39px;
      text-align: center;
      color: $grey-default;

      &:after {
        display: block;
        opacity: 0;
        content: '';
        top: 2px;
        position: absolute;
        right: 2px;
        width: 0;
        height: 0;
        border-top: 6px solid $green-dark;
        border-left: 6px solid transparent;
        transition: opacity 0.5s;
      }

      &:not(.empty):not(.past):not(.disabled):not(.selected) {
        &:hover {
          border: 2px solid $green-dark;
          line-height: 35px;
          border-radius: 3px;
          font-weight: 500;
        }
      }

      &:not(.empty):not(.past):not(.disabled):not(.selected):not(.highlighted) {
        background: #ffffff;
      }

      &.direct:after {
        opacity: 1;
      }

      &:not(.empty):not(.past):not(.disabled) {
        cursor: pointer;

        @include outline-focus;
      }

      &.selected:not(.empty) {
        &.highlighted {
          background-color: rgba(200, 221, 0, 0.1);
        }

        &.end-date {
          background-color: rgba(200, 221, 0, 0.1);
        }

        span {
          background: $green-base;
          border-radius: 4px;
          font-weight: 500;
          width: 100%;
          height: 100%;
          display: inline-block;
        }
      }

      &.highlighted:not(.selected):not(.empty) {
        background-color: rgba(200, 221, 0, 0.1);
        font-weight: 500;
      }

      &.past,
      &.disabled:not(.empty) {
        color: $calendar-disabled-text;
        cursor: default;
      }

      &.direct-flight {
        position: relative;

        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 2px;
          right: 2px;
          width: 0;
          height: 0;
          border-top: 6px solid $green-dark;
          border-left: 6px solid transparent;
          transition: width 2s;
        }
      }
    }
  }
</style>

<style
  lang="scss"
  rel="stylesheet/scss"
>
@import '~assets/styles/components/tooltip.scss';

.js-tooltip {
  &.js-disabledBtn-tooltip,
  &.js-calendar-tooltip {
    @include tooltip();
    max-width: 270px;
    width: 100%;
    text-align: center;
    border: 1px solid $scn-gray-border;
    background-color: white;
    font-size: rem(13, 16);
    line-height: rem(18, 16);
    color: $blue-dark;
    border-radius: 4px;
    margin-bottom: 10px !important;

    .tooltip-cover {
      .tooltip-arrow {
        border-color: rgba($scn-gray-border, 1) transparent transparent;
        border-width: 8px 8px 0;

        &:after {
          content: '';
          position: relative;
          display: block;
          border-color: white transparent transparent;
          border-style: solid;
          top: -9px;
          left: -7px;
          border-width: 7px 7px 0;
        }
      }
    }
  }
}

.js-tooltip.js-disabled-tooltip {
  display: none;
}
</style>
